/**
 * Unified input handler for keyboard and touch controls.
 * Exposes a `state` object consumed each frame by Car.update().
 */
export class Controls {
  constructor() {
    this.state = {
      forward:  false,
      backward: false,
      left:     false,
      right:    false,
      flyUp:    false,  // Space  — climb in airplane mode
      flyDown:  false,  // Shift  — descend in airplane mode
    };

    this._keys   = new Set();
    this._touch  = { forward: false, backward: false, left: false, right: false, flyUp: false, flyDown: false };

    this._setupKeyboard();
    this._setupTouch();
  }

  // ── Keyboard ─────────────────────────────────────────────────────────────

  _setupKeyboard() {
    window.addEventListener('keydown', (e) => {
      // Never steal keys while the user is typing in a form field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      this._keys.add(e.code);
      if (['ArrowUp','ArrowDown','ArrowLeft','ArrowRight'].includes(e.code)) {
        e.preventDefault(); // prevent page scroll; Space no longer intercepted
      }
    });
    window.addEventListener('keyup', (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      this._keys.delete(e.code);
    });
    // Release all keys when window loses focus (alt-tab, etc.)
    window.addEventListener('blur', () => this._keys.clear());
  }

  // ── Touch ─────────────────────────────────────────────────────────────────

  _setupTouch() {
    // Detect touch device and show the virtual controls
    const hasTouchScreen =
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0;

    if (hasTouchScreen) {
      const el = document.getElementById('touch-controls');
      if (el) el.classList.add('visible');
    }

    // Map button id → state key
    const BTN_MAP = {
      'btn-left':     'left',
      'btn-right':    'right',
      'btn-gas':      'forward',
      'btn-brake':    'backward',
      'btn-fly-up':   'flyUp',
      'btn-fly-down': 'flyDown',
    };

    for (const [id, action] of Object.entries(BTN_MAP)) {
      const btn = document.getElementById(id);
      if (!btn) continue;
      this._bindTouchBtn(btn, action);
    }
  }

  /**
   * Bind pointer events to a single touch button.
   * Using Pointer Events (not Touch Events) because they handle
   * mouse, touch and stylus uniformly and support pointer capture.
   */
  _bindTouchBtn(btn, action) {
    // Track how many pointers are currently pressing this button
    const activePointers = new Set();

    const press = (e) => {
      e.preventDefault();
      btn.setPointerCapture(e.pointerId); // keep tracking even if finger slides off
      activePointers.add(e.pointerId);
      this._touch[action] = true;
      btn.classList.add('active');
    };

    const release = (e) => {
      activePointers.delete(e.pointerId);
      if (activePointers.size === 0) {
        this._touch[action] = false;
        btn.classList.remove('active');
      }
    };

    btn.addEventListener('pointerdown',   press);
    btn.addEventListener('pointerup',     release);
    btn.addEventListener('pointercancel', release);
  }

  // ── Per-frame update ──────────────────────────────────────────────────────

  /** Merge keyboard + touch state. Call once per frame. */
  update() {
    const k = this._keys;
    const t = this._touch;
    this.state.forward  = k.has('KeyW') || k.has('ArrowUp')    || t.forward;
    this.state.backward = k.has('KeyS') || k.has('ArrowDown')  || t.backward;
    this.state.left     = k.has('KeyA') || k.has('ArrowLeft')  || t.left;
    this.state.right    = k.has('KeyD') || k.has('ArrowRight') || t.right;
    this.state.flyUp    = k.has('Space')    || t.flyUp;
    this.state.flyDown  = k.has('ShiftLeft') || k.has('ShiftRight') || t.flyDown;
  }
}
